import type { ComponentType } from 'react';
import { useMemo } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { Wizard } from '../../../components/Wizard/Wizard';
import type { KycInitiationTrigger } from '../../../hooks/kyc/types';
import type { RootStore } from '../../../stores/RootStore';
import { useStores } from '../../../stores/useStores';
import { PlayerKycExplanation } from '../PlayerKycExplanation/PlayerKycExplanation';
import { PlayerKycForm } from '../PlayerKycForm/PlayerKycForm';
import { playerKycInvitationFactory } from '../PlayerKycInvitation/PlayerKycInvitation';
import type { EnhancedMessageData } from '../types';

import styles from './PlayerKycWizard.module.scss';

export function playerKycWizardFactory(config: {
  trigger: KycInitiationTrigger;
  classNames?: Partial<Record<'explanationMain' | 'formWrapper' | 'formLoader' | 'formMain', string>>;
  onTokenError?: (stores: RootStore, apiError?: unknown) => void;
  onMessage?: (data: EnhancedMessageData, wizardChildProps: WizardChildProps) => void;
  onBeforePersonalDetailsOpened?: (stores: RootStore) => void;
  onAfterPersonalDetailsClosed?: (stores: RootStore) => void;
  onClose: (stores: RootStore) => void;
}) {
  return observer(function PlayerKycWizard() {
    const { wizardStore } = useStores();

    const wizardSteps = useMemo<ComponentType<WizardChildProps>[]>(
      () => [
        playerKycInvitationFactory(config.onClose),
        function KycFirstStep(props) {
          return (
            <>
              <DialogHeader title="User verification" />
              <DialogContent>
                <PlayerKycExplanation
                  className={config.classNames?.explanationMain}
                  isMountedButHidden={!props.isCurrent}
                  trigger={config.trigger}
                  onAfterPersonalDetailsClosed={config.onAfterPersonalDetailsClosed}
                  onBeforePersonalDetailsOpened={config.onBeforePersonalDetailsOpened}
                  onStartClick={props.nextStep}
                />
              </DialogContent>
            </>
          );
        },
        function KycSecondStep(props) {
          return (
            <>
              <DialogHeader title="User verification" />
              <DialogContent className={styles.formContent}>
                <PlayerKycForm
                  classNames={{
                    wrapper: config.classNames?.formWrapper,
                    form: config.classNames?.formMain,
                    loader: config.classNames?.formLoader,
                  }}
                  isMountedButHidden={!props.isCurrent}
                  onMessage={(data) => config.onMessage?.(data, props)}
                  onTokenError={config.onTokenError}
                />
              </DialogContent>
            </>
          );
        },
      ],
      [],
    );

    return (
      <Wizard
        classNameWrapper={classNames({
          [styles.allowOverflow]: wizardStore.step === 0,
        })}
        steps={wizardSteps}
      />
    );
  });
}
